import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, UserPlus, Search, Check, X, UserMinus, MessageSquare, Loader2, Send, Clock, Swords, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  useFriends,
  usePendingRequests,
  useSentRequests,
  useSearchUsers,
  useSendFriendRequest,
  useAcceptFriendRequest,
  useDeclineFriendRequest,
  useCancelFriendRequest,
  useRemoveFriend,
} from '@/hooks/useFriends';
import { cn } from '@/lib/utils';

type TabId = 'friends' | 'requests' | 'find';

const listItemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.25 },
  }),
  exit: { opacity: 0, x: -20, transition: { duration: 0.2 } },
};

function getInitials(name: string) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function FriendsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('friends');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [expandedFriendId, setExpandedFriendId] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Data hooks
  const { data: friends = [], isLoading: friendsLoading } = useFriends();
  const { data: requests = [], isLoading: requestsLoading } = usePendingRequests();
  const { data: sentRequests = [], isLoading: sentLoading } = useSentRequests();
  const { data: searchResults = [], isLoading: searchLoading } = useSearchUsers(debouncedQuery);

  // Mutations
  const sendRequest = useSendFriendRequest();
  const acceptRequest = useAcceptFriendRequest();
  const declineRequest = useDeclineFriendRequest();
  const cancelRequest = useCancelFriendRequest();
  const removeFriend = useRemoveFriend();

  // Filter out already-friended users from search results
  const friendIds = useMemo(() => new Set(friends.map((f) => f.id)), [friends]);
  const filteredSearchResults = useMemo(
    () => searchResults.filter((u) => !u.isFriend && !friendIds.has(u.id)),
    [searchResults, friendIds]
  );

  const requestsCount = requests.length + sentRequests.length;

  const tabs = [
    { id: 'friends' as TabId, label: 'Friends', icon: <Users className="h-4 w-4" />, count: friends.length },
    { id: 'requests' as TabId, label: 'Requests', icon: <UserPlus className="h-4 w-4" />, count: requestsCount },
    { id: 'find' as TabId, label: 'Find Friends', icon: <Search className="h-4 w-4" />, count: 0 },
  ];

  const toggleExpand = (friendId: string) => {
    setExpandedFriendId((prev) => (prev === friendId ? null : friendId));
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 pb-20 md:p-6 lg:pb-6">
      <div>
        <h1 className="text-2xl font-bold">Friends</h1>
        <p className="text-sm text-muted-foreground">Manage your friends and find new ones</p>
      </div>

      {/* Tab buttons */}
      <div className="flex gap-2">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span className="ml-1.5">{tab.label}</span>
            {tab.count > 0 && (
              <Badge variant="secondary" className="ml-1">
                {tab.count}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Friends Tab */}
      {activeTab === 'friends' && (
        <div className="space-y-3">
          {friendsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : friends.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="mb-3 h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm font-medium text-muted-foreground">No friends yet</p>
                <p className="mt-1 text-xs text-muted-foreground/70">
                  Search for users to add them as friends
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => setActiveTab('find')}
                >
                  <Search className="mr-1.5 h-4 w-4" />
                  Find Friends
                </Button>
              </CardContent>
            </Card>
          ) : (
            <AnimatePresence mode="popLayout">
              {friends.map((friend, i) => {
                const isExpanded = expandedFriendId === friend.id;

                return (
                  <motion.div
                    key={friend.friendshipId}
                    custom={i}
                    variants={listItemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    layout
                  >
                    <Card>
                      <CardContent className="p-4">
                        {/* Main row — always visible */}
                        <div
                          className="flex items-center gap-4 cursor-pointer lg:cursor-default"
                          onClick={() => toggleExpand(friend.id)}
                        >
                          <div className="relative">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={friend.avatarUrl || undefined} alt={friend.displayName} />
                              <AvatarFallback>{getInitials(friend.displayName)}</AvatarFallback>
                            </Avatar>
                            <span
                              className={cn(
                                'absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-background',
                                friend.status === 'online' ? 'bg-green-500' : 'bg-gray-400'
                              )}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{friend.displayName}</p>
                            <p className="text-xs text-muted-foreground">
                              {friend.status === 'online' ? 'Online' : 'Offline'}
                            </p>
                          </div>

                          {/* Desktop: inline buttons (hidden on mobile) */}
                          <div className="hidden lg:flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); navigate(`/profile/${friend.id}`); }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); navigate(`/wagers/create?opponent=${friend.id}&opponentName=${encodeURIComponent(friend.displayName)}`); }}
                            >
                              <Swords className="mr-1.5 h-4 w-4" />
                              Challenge
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); navigate(`/messages/${friend.id}`); }}
                            >
                              <MessageSquare className="mr-1.5 h-4 w-4" />
                              Message
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={(e) => { e.stopPropagation(); removeFriend.mutate(friend.friendshipId); }}
                              disabled={removeFriend.isPending && removeFriend.variables === friend.friendshipId}
                            >
                              {removeFriend.isPending && removeFriend.variables === friend.friendshipId ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <UserMinus className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>

                        {/* Mobile: tap-to-expand action buttons */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden lg:hidden"
                            >
                              <div className="grid grid-cols-2 gap-2 pt-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full"
                                  onClick={() => navigate(`/wagers/create?opponent=${friend.id}&opponentName=${encodeURIComponent(friend.displayName)}`)}
                                >
                                  <Swords className="mr-1.5 h-4 w-4" />
                                  Challenge
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full"
                                  onClick={() => navigate(`/messages/${friend.id}`)}
                                >
                                  <MessageSquare className="mr-1.5 h-4 w-4" />
                                  Message
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full"
                                  onClick={() => navigate(`/profile/${friend.id}`)}
                                >
                                  <Eye className="mr-1.5 h-4 w-4" />
                                  Profile
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full text-destructive hover:text-destructive"
                                  onClick={() => removeFriend.mutate(friend.friendshipId)}
                                  disabled={removeFriend.isPending && removeFriend.variables === friend.friendshipId}
                                >
                                  {removeFriend.isPending && removeFriend.variables === friend.friendshipId ? (
                                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                                  ) : (
                                    <UserMinus className="mr-1.5 h-4 w-4" />
                                  )}
                                  Remove
                                </Button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-6">
          {/* Incoming Requests */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Incoming Requests
              {requests.length > 0 && (
                <Badge variant="secondary" className="ml-2">{requests.length}</Badge>
              )}
            </h2>
            {requestsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : requests.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                  <UserPlus className="mb-3 h-8 w-8 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">No incoming requests</p>
                </CardContent>
              </Card>
            ) : (
              <AnimatePresence mode="popLayout">
                {requests.map((request, i) => {
                  const isAccepting = acceptRequest.isPending && acceptRequest.variables === request.id;
                  const isDeclining = declineRequest.isPending && declineRequest.variables === request.id;
                  const isBusy = isAccepting || isDeclining;

                  return (
                    <motion.div
                      key={request.id}
                      custom={i}
                      variants={listItemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      layout
                    >
                      <Card>
                        <CardContent className="flex items-center gap-4 p-4">
                          <Avatar className="h-10 w-10">
                            <AvatarImage
                              src={request.requester.avatarUrl || undefined}
                              alt={request.requester.displayName}
                            />
                            <AvatarFallback>{getInitials(request.requester.displayName)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{request.requester.displayName}</p>
                            <p className="text-xs text-muted-foreground">
                              Sent {new Date(request.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => acceptRequest.mutate(request.id)}
                              disabled={isBusy}
                            >
                              {isAccepting ? (
                                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="mr-1.5 h-4 w-4" />
                              )}
                              Accept
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => declineRequest.mutate(request.id)}
                              disabled={isBusy}
                            >
                              {isDeclining ? (
                                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                              ) : (
                                <X className="mr-1.5 h-4 w-4" />
                              )}
                              Decline
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </div>

          {/* Sent Requests */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Sent Requests
              {sentRequests.length > 0 && (
                <Badge variant="secondary" className="ml-2">{sentRequests.length}</Badge>
              )}
            </h2>
            {sentLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : sentRequests.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                  <Send className="mb-3 h-8 w-8 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">No pending sent requests</p>
                </CardContent>
              </Card>
            ) : (
              <AnimatePresence mode="popLayout">
                {sentRequests.map((request, i) => {
                  const isCancelling = cancelRequest.isPending && cancelRequest.variables === request.id;

                  return (
                    <motion.div
                      key={request.id}
                      custom={i}
                      variants={listItemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      layout
                    >
                      <Card>
                        <CardContent className="flex items-center gap-4 p-4">
                          <Avatar className="h-10 w-10">
                            <AvatarImage
                              src={request.addressee.avatarUrl || undefined}
                              alt={request.addressee.displayName}
                            />
                            <AvatarFallback>{getInitials(request.addressee.displayName)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{request.addressee.displayName}</p>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>Pending since {new Date(request.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => cancelRequest.mutate(request.id)}
                            disabled={isCancelling}
                          >
                            {isCancelling ? (
                              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                            ) : (
                              <X className="mr-1.5 h-4 w-4" />
                            )}
                            Cancel
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </div>
        </div>
      )}

      {/* Find Friends Tab */}
      {activeTab === 'find' && (
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {debouncedQuery.length < 2 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Search className="mb-3 h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm font-medium text-muted-foreground">Search for users</p>
                <p className="mt-1 text-xs text-muted-foreground/70">
                  Type at least 2 characters to search
                </p>
              </CardContent>
            </Card>
          ) : searchLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSearchResults.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="mb-3 h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm font-medium text-muted-foreground">No users found</p>
                <p className="mt-1 text-xs text-muted-foreground/70">
                  Try a different search term
                </p>
              </CardContent>
            </Card>
          ) : (
            <AnimatePresence mode="popLayout">
              {filteredSearchResults.map((user, i) => {
                const isSending = sendRequest.isPending && sendRequest.variables === user.id;

                return (
                  <motion.div
                    key={user.id}
                    custom={i}
                    variants={listItemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    layout
                  >
                    <Card>
                      <CardContent className="flex items-center gap-4 p-4">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.avatarUrl || undefined} alt={user.displayName} />
                          <AvatarFallback>{getInitials(user.displayName)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user.displayName}</p>
                        </div>
                        <div>
                          {user.hasPendingRequest ? (
                            <Button variant="outline" size="sm" disabled className="gap-1.5">
                              <Clock className="h-4 w-4" />
                              Request Sent
                            </Button>
                          ) : (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => sendRequest.mutate(user.id)}
                              disabled={isSending}
                            >
                              {isSending ? (
                                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                              ) : (
                                <UserPlus className="mr-1.5 h-4 w-4" />
                              )}
                              Add Friend
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      )}
    </div>
  );
}
